import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formatDistanceToNow } from "date-fns";
import {
  CheckIcon,
  KeyRoundIcon,
  Loader2Icon,
  MailIcon,
  ShieldAlertIcon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PasswordInput } from "@/components/ui/password-input";
import { useUser } from "@/hooks/useUserProvider";
import {
  SecurityRequestError,
  useChangePassword,
  useSendPasswordResetEmail,
  useSendVerificationEmail,
  useSetPassword,
} from "@/hooks/queries/securityQueries";
import {
  newPasswordSchema,
  passwordRules,
  passwordStrength,
} from "@/lib/validation/password";
import { cn } from "@/lib/utils";
import { SecuritySection } from "./Section";
import { providerName } from "./describe";

const SIGN_IN_PROVIDERS = ["google", "apple", "github", "gitlab"];

const passwordsMatch = (
  values: { newPassword: string; confirmPassword: string },
  ctx: z.RefinementCtx,
) => {
  if (values.confirmPassword !== values.newPassword) {
    ctx.addIssue({
      code: "custom",
      message: "The passwords do not match",
      path: ["confirmPassword"],
    });
  }
};

const changeSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .superRefine(passwordsMatch);

const setSchema = z
  .object({
    currentPassword: z.string().optional(),
    newPassword: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .superRefine(passwordsMatch);

type ChangeValues = z.infer<typeof setSchema>;

const StrengthMeter = ({ value }: { value: string }) => {
  const strength = passwordStrength(value);
  return (
    <div className="flex flex-col gap-2">
      <Progress
        value={strength}
        className={cn("h-1.5", {
          "[&>div]:bg-red-500": strength > 0 && strength < 50,
          "[&>div]:bg-amber-500": strength >= 50 && strength < 80,
          "[&>div]:bg-emerald-500": strength >= 80,
        })}
      />
      <ul className="grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
        {passwordRules.map(rule => {
          const ok = value ? rule.test(value) : false;
          return (
            <li
              key={rule.label}
              className={cn(
                "flex items-center gap-1.5",
                ok ? "text-emerald-400" : "text-muted-foreground",
              )}
            >
              <CheckIcon className={cn("size-3", !ok && "opacity-30")} />
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const PasswordSection = () => {
  const { user, logout } = useUser();
  const [editing, setEditing] = useState(false);
  const changePassword = useChangePassword();
  const setPassword = useSetPassword();
  const sendReset = useSendPasswordResetEmail();
  const sendVerification = useSendVerificationEmail();

  const hasPassword = user?.hasCustomPassword !== false;
  const signInMethods = SIGN_IN_PROVIDERS.filter(
    provider => (user?.socials?.[provider]?.length ?? 0) > 0,
  );

  const form = useForm<ChangeValues>({
    resolver: zodResolver(hasPassword ? changeSchema : setSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });
  const newPassword = form.watch("newPassword");

  const close = () => {
    setEditing(false);
    form.reset();
    changePassword.reset();
    setPassword.reset();
  };

  const submit = async (values: ChangeValues) => {
    try {
      if (hasPassword) {
        await changePassword.mutateAsync({
          currentPassword: values.currentPassword ?? "",
          newPassword: values.newPassword,
        });
        toast.success("Password changed", {
          description: "Every other device was signed out.",
        });
      } else {
        await setPassword.mutateAsync(values.newPassword);
        toast.success("Password added", {
          description: "You can now sign in with your email and password too.",
        });
      }
      close();
    } catch (err) {
      if (
        err instanceof SecurityRequestError &&
        err.code === "REAUTH_REQUIRED"
      ) {
        toast.info("Sign in again to add a password", {
          description: `For your security, sign in with ${signInMethods.map(providerName).join(" or ") || "your provider"} and try again.`,
          action: { label: "Sign in again", onClick: () => logout() },
        });
      }
    }
  };

  const mutation = hasPassword ? changePassword : setPassword;
  const lastChanged = user?.passwordChangedAt
    ? formatDistanceToNow(new Date(user.passwordChangedAt), { addSuffix: true })
    : null;

  return (
    <SecuritySection
      icon={KeyRoundIcon}
      title="Sign-in & password"
      description={
        hasPassword
          ? lastChanged
            ? `Password last changed ${lastChanged}.`
            : "You sign in with your email and a password."
          : `You sign in with ${signInMethods.map(providerName).join(" and ") || "a connected account"}. Add a password to also sign in with your email.`
      }
      action={
        editing ? null : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditing(true)}
          >
            {hasPassword ? "Change password" : "Add a password"}
          </Button>
        )
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {hasPassword ? (
          <Badge variant="secondary">Email &amp; password</Badge>
        ) : null}
        {signInMethods.map(provider => (
          <Badge key={provider} variant="secondary">
            {providerName(provider)}
          </Badge>
        ))}
        {user?.isActive ? (
          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/10">
            Email verified
          </Badge>
        ) : (
          <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/10">
            Email not verified
          </Badge>
        )}
      </div>

      {!user?.isActive ? (
        <Alert className="border-amber-500/30 bg-amber-500/5">
          <ShieldAlertIcon className="size-4 text-amber-300" />
          <AlertDescription className="flex flex-col gap-2 text-sm md:flex-row md:items-center md:justify-between">
            <span>
              Confirm {user?.email} to protect your account and join teams.
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={
                sendVerification.isPending || sendVerification.isSuccess
              }
              onClick={() =>
                sendVerification.mutate(undefined, {
                  onSuccess: () => toast.success("Verification email sent"),
                  onError: err => toast.error(err.message),
                })
              }
            >
              {sendVerification.isSuccess ? "Email sent" : "Resend email"}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {editing ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(submit)}
            className="border-border/60 flex flex-col gap-4 rounded-lg border p-4"
          >
            {hasPassword ? (
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                        showStrength={false}
                        autoComplete="current-password"
                        placeholder="Your current password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        value={field.value}
                        onValueChange={field.onChange}
                        showStrength={false}
                        autoComplete="new-password"
                        placeholder="New password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm new password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        value={field.value}
                        onValueChange={field.onChange}
                        showStrength={false}
                        autoComplete="new-password"
                        placeholder="Repeat it"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <StrengthMeter value={newPassword} />
            {mutation.isError &&
            !(
              mutation.error instanceof SecurityRequestError &&
              mutation.error.code === "REAUTH_REQUIRED"
            ) ? (
              <Alert variant="destructive" className="border-none p-0">
                <AlertDescription>{mutation.error.message}</AlertDescription>
              </Alert>
            ) : null}
            <p className="text-muted-foreground text-xs">
              Changing your password signs out every other device.
            </p>
            <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between">
              {hasPassword ? (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto justify-start px-0 text-xs"
                  disabled={sendReset.isPending || sendReset.isSuccess}
                  onClick={() =>
                    sendReset.mutate(undefined, {
                      onSuccess: () =>
                        toast.success("Check your inbox", {
                          description:
                            "We sent a link to choose a new password.",
                        }),
                      onError: err => toast.error(err.message),
                    })
                  }
                >
                  <MailIcon className="size-3.5" />
                  {sendReset.isSuccess
                    ? "Reset link sent"
                    : "Forgot it? Email me a reset link"}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={close}>
                  Cancel
                </Button>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? (
                    <Loader2Icon className="animate-spin" />
                  ) : null}
                  {hasPassword ? "Update password" : "Add password"}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      ) : null}
    </SecuritySection>
  );
};
